import React, { useState } from 'react';
import { X, Download, Loader2, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { toast } from '../hooks/use-toast';

const AccountSettings = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const [exporting, setExporting] = useState(false);

  if (!open || !user) return null;

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/account/export`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `capcute-export-${Date.now()}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast({ title: lang === 'sv' ? 'Nedladdning startad!' : 'بدأ التنزيل!' });
    } catch (e) {
      toast({ title: 'Export misslyckades' });
    } finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><User className="w-4 h-4 text-white" /></div>
            <div className="font-black text-neutral-900 text-[16px]">{lang === 'sv' ? 'Konto' : 'الحساب'}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            {user.picture ? <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-2xl object-cover" referrerPolicy="no-referrer" /> : <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white text-2xl font-black">{user.name?.[0] || 'U'}</div>}
            <div className="flex-1 min-w-0">
              <div className="font-black text-lg text-neutral-900 truncate">{user.name}</div>
              <div className="text-[13px] text-neutral-500 truncate">{user.email}</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">{lang === 'sv' ? 'Data' : 'البيانات'}</div>
            <button onClick={exportData} disabled={exporting} className="w-full flex items-center gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 transition-colors disabled:opacity-50">
              <div className="w-9 h-9 rounded-lg bg-cyan-500 flex items-center justify-center">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Download className="w-4 h-4 text-white" />}
              </div>
              <div className="flex-1 text-left rtl:text-right">
                <div className="font-bold text-[13.5px] text-neutral-900">{exporting ? (lang === 'sv' ? 'Förbereder ZIP...' : 'يجهز ZIP...') : (lang === 'sv' ? 'Ladda ner min data' : 'تنزيل بياناتي')}</div>
                <div className="text-[11.5px] text-neutral-500">{lang === 'sv' ? 'Videor, projekt, kommentarer och profil som ZIP' : 'الفيديوهات والمشاريع والتعليقات كملف ZIP'}</div>
              </div>
            </button>
          </div>

          <button onClick={() => { logout(); onClose(); }} className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="font-bold text-[13.5px]">{lang === 'sv' ? 'Logga ut' : 'تسجيل الخروج'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
